import { useContext, useState, useEffect } from "react";
import styles from "./NewEvent.module.scss";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate } from "react-router-dom";
import { DateTimePicker } from "@mui/x-date-pickers";
import { addHours } from "date-fns";
import { useAlert } from "../context/AlertContext";
import { useEnumStore } from "../store/enumStore";
import { fetcher } from "../utils/fetcher";
import { InputAddress } from "../components/InputAddress/InputAddress";
import { AuthContext } from "../context/AuthContext";
import { EventImagesSection } from "../components/EventImagesSection/EventImagesSection";
import { GalleryModal } from "../components/GalleryModal/GalleryModal";
import { EventInterestsSkillsSection } from "../components/EventInterestsSkillsSection/EventInterestsSkillsSection";
import { useTranslation } from "../i18n";
import { getEventTypeLabel } from "../utils/enumHelpers";

interface EventImage {
  id: string;
  public_link: string;
}

interface Document {
  id: string;
  name: string;
  public_link: string;
  type: string;
}

interface FormData {
  event_title: string;
  event_type: string;
  skills: string[];
  interests: string[];
  event_start_datetime: Date | null;
  event_end_datetime: Date | null;
  address: string;
  details: string;
  documentation: string[];
  capacity: number;
  is_urgent: boolean;
  event_image: EventImage[];
}

interface NewEventGalleryState {
  modalOpen: boolean;
  loading: boolean;
  images: EventImage[];
}

export const NewEvent = () => {
  const navigate = useNavigate();
  const skills = useEnumStore((state) => state.skills);
  const interests = useEnumStore((state) => state.interests);
  const eventTypes = useEnumStore((state) => state.eventTypes);
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.currentUser;
  const { setAlert } = useAlert();
  const { t } = useTranslation();

  const [formData, setFormData] = useState<FormData>({
    event_title: "",
    event_type: "in_person",
    skills: [],
    interests: [],
    event_start_datetime: null,
    event_end_datetime: null,
    address: "",
    details: "",
    documentation: [],
    capacity: 15,
    is_urgent: false,
    event_image: [],
  });

  const [gallery, setGallery] = useState<NewEventGalleryState>({
    modalOpen: false,
    loading: false,
    images: [],
  });
  const [documents, setDocuments] = useState<Document[] | null>(null);
  const [selectedImages, setSelectedImages] = useState<EventImage[]>([]);
  const [eventsContainingImage, setEventsContainingImage] = useState<any[]>([]);
  const [eventCreationLoading, setEventCreationLoading] =
    useState<boolean>(false);

  const organisation_id = currentUser?.userId;
  const minTextLength = 100;
  const remainingCharacters = Math.max(
    0,
    minTextLength - formData.details.length,
  );
  const maxUploadingImages = 30 - gallery.images?.length;

  const isFormValid =
    formData.event_title?.trim() !== "" &&
    formData.event_type &&
    formData.skills.length > 0 &&
    formData.interests.length > 0 &&
    formData.event_start_datetime &&
    formData.event_end_datetime &&
    formData.address?.trim() !== "" &&
    formData.details.length >= minTextLength &&
    formData.capacity > 0;

  const fetchOrgsGallery = async () => {
    if (!organisation_id) return;

    try {
      setGallery((prevState) => ({ ...prevState, loading: true }));
      const data = await fetcher<EventImage[]>({
        url: `/organisation/get-organisation-images/${organisation_id}`,
      });
      setGallery((prevState) => ({ ...prevState, images: data || [] }));
    } catch (error) {
      console.error("Error fetching organisation's images:", error);
      setAlert(true, "error", t("newEvent.alerts.imagesLoadFailed"));
    } finally {
      setGallery((prevState) => ({ ...prevState, loading: false }));
    }
  };

  const fetchOrgsDocuments = async () => {
    if (!currentUser?.userId) return;

    try {
      const data = await fetcher<Document[]>({
        url: `/organisation/get-documents/${currentUser.userId}`,
      });
      setDocuments(data || []);
    } catch (error) {
      console.error("Error fetching organisation's documents:", error);
      setAlert(true, "error", t("newEvent.alerts.documentsLoadFailed"));
    }
  };

  useEffect(() => {
    if (organisation_id) {
      fetchOrgsGallery();
      fetchOrgsDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisation_id]);

  const handleCheckboxToggle = (
    value: string,
    field: "skills" | "interests" | "documentation",
  ) => {
    const strValue = String(value);
    const alreadyExists = formData[field]?.includes(strValue);
    if (alreadyExists) {
      setFormData((prevState) => ({
        ...prevState,
        [field]: prevState[field].filter((item) => item !== strValue),
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [field]: [...prevState[field], strValue],
      }));
    }
  };

  const isImageSelected = (imageId: string) => {
    return selectedImages.some((img) => img.id === imageId);
  };

  const handleImageUpload = async (files: File[]) => {
    if (!organisation_id) return;

    const formDataUpload = new FormData();
    files.forEach((file) => {
      formDataUpload.append("files", file);
    });

    try {
      await fetcher<void>({
        url: `/organisation/upload-image-to-gallery/${organisation_id}`,
        method: "POST",
        body: formDataUpload,
        noContentType: true,
      });
      await fetchOrgsGallery();
      setAlert(true, "success", t("newEvent.alerts.uploadSuccess"));
    } catch (error) {
      setAlert(
        true,
        "error",
        t("newEvent.alerts.uploadFailed"),
      );
      console.error("Error uploading images:", error);
    }
  };

  const handleImageSelect = async (image: EventImage) => {
    const alreadyExists = isImageSelected(image.id);
    if (alreadyExists) {
      setSelectedImages((prevState) => [
        ...prevState.filter((img) => img.id !== image.id),
      ]);
    } else setSelectedImages((prevState) => [...prevState, image]);
  };

  const handleCheckImageInEvents = async (image: EventImage) => {
    if (!organisation_id) return;

    try {
      const data = await fetcher<any[]>({
        url: `/event/image-related-events/${organisation_id}?image_id=${image.id}`,
      });
      setEventsContainingImage(data || []);
    } catch (error) {
      setAlert(
        true,
        "error",
        t("newEvent.alerts.deleteCheckFailed"),
      );
      console.error("Error checking image in events:", error);
    }
  };

  const handleImageDelete = async (image: EventImage) => {
    if (!organisation_id) return;

    try {
      await fetcher<void>({
        url: `/organisation/delete-from-image-gallery/${organisation_id}?image_id=${image.id}`,
        method: "DELETE",
      });
      await fetchOrgsGallery();
      setAlert(true, "success", t("newEvent.alerts.deleteSuccess"));
    } catch (error) {
      setAlert(
        true,
        "error",
        t("newEvent.alerts.deleteFailed"),
      );
      console.error("Error deleting image:", error);
    }
  };

  const handleRemoveImage = (imageId: string | number) => {
    setFormData((prevState) => ({
      ...prevState,
      event_image: prevState.event_image.filter((img) => img.id !== imageId),
    }));
    setSelectedImages((prevState) =>
      prevState.filter((img) => img.id !== imageId),
    );
  };

  const handleOpenGallery = () => {
    setGallery((prevState) => ({ ...prevState, modalOpen: true }));
  };

  const handleCloseGallery = () => {
    setGallery((prevState) => ({ ...prevState, modalOpen: false }));
  };

  const handleAssignImages = () => {
    setFormData((prevState) => ({
      ...prevState,
      event_image: selectedImages,
    }));
  };

  const handleCreateEvent = async () => {
    setEventCreationLoading(true);
    const newData = {
      ...formData,
      // skills and interests are already strings, backend expects strings
      event_image: selectedImages?.map((img) => {
        return { id: img.id, public_link: img.public_link };
      }),
    };
    try {
      await fetcher<void>({
        url: "/event",
        method: "POST",
        body: JSON.stringify(newData),
      });
      navigate("/organisation/events");
      setAlert(
        true,
        "success",
        t("newEvent.alerts.published", { title: formData.event_title }),
      );
    } catch (error) {
      console.error("Error creating new event:", error);
      setAlert(
        true,
        "error",
        t("newEvent.alerts.publishFailed"),
      );
    } finally {
      setEventCreationLoading(false);
    }
  };

  return (
    <div className={styles.newEvent}>
      <Button
        variant="text"
        size="small"
        onClick={() => navigate("/organisation/events")}
      >
        <ArrowBackIosIcon />
        {t("newEvent.backToEvents")}
      </Button>
      <div className={styles.separator}></div>
      <form className={styles.form}>
        <div className={styles.flex}>
          <TextField
            id="event_title"
            type="text"
            label={t("newEvent.title")}
            variant="outlined"
            size="small"
            fullWidth
            value={formData.event_title}
            onChange={(e) =>
              setFormData((prevState) => ({
                ...prevState,
                event_title: e.target.value,
              }))
            }
            required
          />
          <TextField
            id="capacity"
            type="number"
            label={t("newEvent.capacity")}
            variant="outlined"
            size="small"
            value={formData.capacity.toString()}
            onChange={(e) =>
              setFormData((prevState) => ({
                ...prevState,
                capacity: parseInt(e.target.value),
              }))
            }
            required
          />
        </div>
        <div className={styles.flex}>
          <FormControl>
            <InputLabel id="event-type-label">{t("newEvent.type")}</InputLabel>
            <Select
              labelId="event-type-label"
              id="event_type"
              label={t("newEvent.type")}
              size="small"
              value={formData.event_type}
              onChange={(e) =>
                setFormData((prevState) => ({
                  ...prevState,
                  event_type: e.target.value,
                }))
              }
              required
            >
              {eventTypes?.length > 0 ? (
                eventTypes?.map((type) => (
                  <MenuItem
                    key={type.event_type}
                    value={type.event_type}
                    disabled={type.event_type !== "in_person"}
                  >
                    {getEventTypeLabel(type)}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value={"in_person"}>
                  {t("newEvent.typeInPerson")}
                </MenuItem>
              )}
            </Select>
          </FormControl>
          {/* @ts-ignore - JS component */}
          <InputAddress
            editMode={true}
            label={t("newEvent.address")}
            address={{ address_text: formData.address }}
            onAddressChange={(value: string | null | undefined) =>
              setFormData((prevData) => ({
                ...prevData,
                address: value || "",
              }))
            }
            required
          />
        </div>
        <div className={styles.flex}>
          <DateTimePicker
            label={t("newEvent.start")}
            views={["year", "day", "hours", "minutes"]}
            disablePast
            minDateTime={addHours(new Date(), 24)}
            slotProps={{
              textField: { size: "small", required: true },
            }}
            value={formData.event_start_datetime || undefined}
            onChange={(newValue) =>
              setFormData((prevState) => ({
                ...prevState,
                event_start_datetime: newValue,
              }))
            }
          />
          <DateTimePicker
            label={t("newEvent.end")}
            views={["year", "day", "hours", "minutes"]}
            disablePast
            minDateTime={formData.event_start_datetime || undefined}
            slotProps={{
              textField: { size: "small", required: true },
            }}
            value={formData.event_end_datetime || undefined}
            onChange={(newValue) =>
              setFormData((prevState) => ({
                ...prevState,
                event_end_datetime: newValue,
              }))
            }
          />
        </div>
        <EventInterestsSkillsSection
          title={t("newEvent.focus")}
          items={interests as any}
          selectedIds={formData.interests}
          onToggle={(id: string) => handleCheckboxToggle(id, "interests")}
          editMode={true}
          type="interests"
        />
        <EventInterestsSkillsSection
          title={t("newEvent.skills")}
          items={skills as any}
          selectedIds={formData.skills}
          onToggle={(id: string) => handleCheckboxToggle(id, "skills")}
          editMode={true}
          type="skills"
        />
        <div>
          <h4>{t("newEvent.documents")}</h4>
          <div className={styles.group}>
            {documents?.length &&
              documents.length > 0 &&
              documents?.map((doc) => {
                return (
                  <FormControlLabel
                    key={doc.id}
                    control={
                      <Checkbox
                        checked={formData.documentation?.includes(doc.id)}
                        onChange={() =>
                          handleCheckboxToggle(doc.id, "documentation")
                        }
                        name={doc.id}
                      />
                    }
                    label={doc.name}
                  />
                );
              })}
          </div>
        </div>
        <div className={styles.flex}>
          <TextField
            id="details"
            label={t("newEvent.description")}
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={formData.details}
            onChange={(e) =>
              setFormData((prevState) => ({
                ...prevState,
                details: e.target.value,
              }))
            }
            helperText={
              formData.details.length < minTextLength
                ? t("newEvent.charsRemaining", { count: remainingCharacters })
                : t("common.minLengthReached")
            }
            required
          />
        </div>
        {/* @ts-ignore - JS component */}
        <EventImagesSection
          eventImages={formData.event_image}
          onRemoveImage={handleRemoveImage}
          onOpenGallery={handleOpenGallery}
        />

        {/* @ts-ignore */}
        <GalleryModal
          isOpen={gallery.modalOpen}
          onClose={handleCloseGallery}
          gallery={{ loading: gallery.loading, images: gallery.images as any }}
          selectedImages={selectedImages as any}
          onImageSelect={handleImageSelect as any}
          isImageSelected={isImageSelected as any}
          onCheckImageInEvents={handleCheckImageInEvents as any}
          maxUploadingImages={maxUploadingImages}
          onImageUpload={handleImageUpload as any}
          onAssignImages={handleAssignImages as any}
          eventsContainingImage={eventsContainingImage as any}
          onImageDelete={handleImageDelete as any}
        />

        <div className={styles.actions}>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => navigate("/organisation/events")}
          >
            {t("common.cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleCreateEvent}
            disabled={!isFormValid}
            loading={eventCreationLoading}
          >
            {t("newEvent.publish")}
          </Button>
        </div>
      </form>
    </div>
  );
};
